import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthLayout } from './AuthLayout';
import { OAuthButtons } from './OAuthButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { SignupSchema, type SignupValues } from './schemas';
import { signup } from '@/api/auth';
import { applyServerErrors } from './parseApiError';

export function SignupPage() {
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupValues>({
    resolver: zodResolver(SignupSchema),
    defaultValues: { email: '', password: '', confirm: '', consent: false },
  });

  const onSubmit = async (values: SignupValues) => {
    try {
      await signup({ email: values.email, password: values.password });
      toast.success("You're in. Welcome to Kidzio.");
      navigate('/', { replace: true });
    } catch (err) {
      applyServerErrors(err, setError);
    }
  };

  return (
    <AuthLayout
      title="Create your family account"
      subtitle="Free forever for one child. No credit card."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-[color:var(--kz-color-brand-primary)] hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <OAuthButtons />

      <div className="my-6 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs uppercase tracking-wide text-[color:var(--kz-color-ink-muted)]">
          or with email
        </span>
        <Separator className="flex-1" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-4">
        <Field label="Email" htmlFor="email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
        </Field>

        <Field label="Password" htmlFor="password" error={errors.password?.message}>
          <div className="relative">
            <Input
              id="password"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <button
              type="button"
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--kz-color-ink-secondary)]"
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
            10+ characters. Passphrases are great.
          </p>
        </Field>

        <Field label="Confirm password" htmlFor="confirm" error={errors.confirm?.message}>
          <Input
            id="confirm"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            aria-invalid={!!errors.confirm}
            {...register('confirm')}
          />
        </Field>

        <label className="mt-1 flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-[color:var(--kz-color-border-default)]"
            aria-invalid={!!errors.consent}
            {...register('consent')}
          />
          <span className="text-[color:var(--kz-color-ink-secondary)]">
            I&rsquo;ve read the{' '}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/terms" className="underline">
              Terms
            </Link>
            .
          </span>
        </label>
        {errors.consent?.message && (
          <p className="-mt-2 text-xs text-[color:var(--kz-color-status-danger)]">
            {errors.consent.message}
          </p>
        )}

        {errors.root?.serverError?.message && (
          <p className="rounded-control border border-[color:var(--kz-color-status-danger)]/30 bg-[color:var(--kz-color-feedback-danger-bg)] p-3 text-sm text-[color:var(--kz-color-status-danger)]">
            {errors.root.serverError.message}
          </p>
        )}

        <Button type="submit" size="lg" disabled={isSubmitting} className="mt-2">
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" />
              Creating your account
            </>
          ) : (
            'Create account'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-xs text-[color:var(--kz-color-status-danger)]">{error}</p>}
    </div>
  );
}
